import React, { FC } from 'react'
import Collapse from 'blocks.simple/collapse/collapse'
import EditText from 'blocks.simple/editText/editText'
import Icon from 'blocks.simple/icon/icon'
import Button from 'blocks.simple/button/button'
import Tippy from 'blocks.simple/tippy/tippy'
import helpers from 'core/helpers'
import translate from 'core/translate'
import styles_ from '../settings.jcss'
import { cn } from 'ethcss'
import rawStyles from './settings__emails.jcss'
import { grid } from 'theme'
import { isEqual } from 'lodash'
import { NotificationCenterSettings, getInitialLifespanState, useNotificationsEmail } from './settings__emails__hooks'
import Modal from '../../../blocks.simple/modal/modal'
import { text as textStyles } from 'theme'

const maxNotificationsEmails = 5
const styles = (rawStyles as unknown) as Record<string, string>

interface ISettingsEmailProps {
    notificationsEmail: string[]
    notificationCenter?: NotificationCenterSettings
}

export const SettingsEmails: FC<ISettingsEmailProps> = ({ notificationsEmail, notificationCenter }) => {
    const url = window.location.hash
    const query = url.split('?')[1] || ''
    const params = Object.fromEntries(new URLSearchParams(query))
    const {
        state,
        updateNotificationsEmails,
        deleteMail,
        addMail,
        saveEmail,
        confirmDelete,
        collapseRef,
        onChangeLifespan,
        handleFocus,
        onCancelEmailChange,
        onConfirmEmailChange,
        emailRefs,
        focusByRefKey,
    } = useNotificationsEmail({
        notificationsEmail,
        notificationCenter,
    })

    const initialLifespan = getInitialLifespanState(notificationCenter)
    const hasLifespanChanges = !isEqual(state.lifespanD, initialLifespan)
    const hasEmailChanges = !isEqual(state.notificationsEmails, state.notificationsEmailsOriginal)

    return (
        <Collapse
            refObj={collapseRef}
            title={() => translate('forNotifications')}
            className={cn(styles_.background, grid.mb_md)}
            initialOpen={!!(params && params.open && params.open === 'notifications')}
        >
            {!!state.notificationsEmails.length && (
                <div className={styles.emails}>
                    {state.notificationsEmails.map((notificationsEmail: string, index: number) => (
                        <div className={styles.email} key={`notificationsEmail_${index}`}>
                            <div className={grid.space}>
                                <EditText
                                    ref={(el) => {
                                        emailRefs.current[index] = el
                                    }}
                                    value={notificationsEmail}
                                    placeholder={translate('enterYourEmail')}
                                    mod="withBorder"
                                    onChange={(value: string) => updateNotificationsEmails(value, index)}
                                    error={
                                        state.notificationsEmailError?.[index] ? translate('emailFormatError') : null
                                    }
                                    onFocus={() => {
                                        handleFocus(index)
                                    }}
                                    onBlur={() => {
                                        handleFocus(-1)
                                    }}
                                    onEnterKeyDown={() => {
                                        onConfirmEmailChange()
                                    }}
                                    disabled={!helpers.isAvailable({ key: 'settings', action: 'update' })}
                                />
                            </div>
                            {helpers.isAvailable({ key: 'settings', action: 'update' }) &&
                                state.emailEditIndex !== index && (
                                    <div className={cn(styles.editButtons)}>
                                        <div className={styles.delete}>
                                            <Icon type={'pencil'} onClick={() => focusByRefKey(index)} />
                                        </div>
                                        <div className={styles.delete}>
                                            <Icon type={'delete'} onClick={() => confirmDelete(index)} />
                                        </div>
                                    </div>
                                )}
                            {state.emailEditIndex === index && (
                                <div className={cn(styles.editButtons)}>
                                    <div onMouseDown={() => onConfirmEmailChange()}>
                                        <Icon type={'checked'} />
                                    </div>
                                    <div
                                        onMouseDown={(e) => {
                                            e.preventDefault()
                                            onCancelEmailChange(index)
                                        }}
                                    >
                                        <Icon type={'close'} />
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <div className={cn(styles.lifespanRow)}>
                <div className={cn(grid.pb_md, grid.w100)}>
                    <div className={cn(grid.pb_mini)}>{translate('notificationsLifespan')}:</div>
                    <EditText
                        ref={(el) => {
                            emailRefs.current.systemNotification = el
                        }}
                        min={0}
                        tabindex={'0'}
                        className={cn(textStyles.center, styles.lifespanInput)}
                        value={state.lifespanD.systemNotification}
                        indentSize={'mini'}
                        type={'number'}
                        mod="withLightBorder"
                        onChange={(value: number) => {
                            onChangeLifespan(value, 'systemNotification')
                        }}
                        onFocus={() => {
                            handleFocus(-1, 'systemNotification')
                        }}
                        onBlur={() => {
                            handleFocus(-1, '')
                        }}
                        onEnterKeyDown={() => {
                            onConfirmEmailChange()
                        }}
                    />
                </div>
                {state.editingLifespanInput !== 'systemNotification' && (
                    <div className={styles.delete}>
                        <Icon type={'pencil'} onClick={() => focusByRefKey('systemNotification')} />
                    </div>
                )}
                {state.editingLifespanInput === 'systemNotification' && (
                    <div className={cn(styles.editButtons)}>
                        <div onMouseDown={() => onConfirmEmailChange()}>
                            <Icon type={'checked'} />
                        </div>
                        <div onMouseDown={() => onCancelEmailChange()}>
                            <Icon type={'close'} />
                        </div>
                    </div>
                )}
            </div>
            <div className={cn(styles.lifespanRow)}>
                <div className={cn(grid.pb_md, grid.w100)}>
                    <div className={cn(grid.pb_mini)}>{translate('historyLifespan')}:</div>
                    <EditText
                        ref={(el) => {
                            emailRefs.current.history = el
                        }}
                        min={0}
                        tabindex={'0'}
                        className={cn(textStyles.center, styles.lifespanInput)}
                        value={state.lifespanD.history}
                        indentSize={'mini'}
                        type={'number'}
                        mod="withLightBorder"
                        onChange={(value: number) => {
                            onChangeLifespan(value, 'history')
                        }}
                        onFocus={() => {
                            handleFocus(-1, 'history')
                        }}
                        onBlur={() => {
                            handleFocus(-1, '')
                        }}
                        onEnterKeyDown={() => {
                            onConfirmEmailChange()
                        }}
                    />
                </div>
                {state.editingLifespanInput !== 'history' && (
                    <div className={styles.delete}>
                        <Icon type={'pencil'} onClick={() => focusByRefKey('history')} />
                    </div>
                )}
                {state.editingLifespanInput === 'history' && (
                    <div className={cn(styles.editButtons)}>
                        <div onMouseDown={() => onConfirmEmailChange()}>
                            <Icon type={'checked'} />
                        </div>
                        <div onMouseDown={() => onCancelEmailChange()}>
                            <Icon type={'close'} />
                        </div>
                    </div>
                )}
            </div>
            {/*<div className={cn(styles.lifespanRow)}>*/}
            {/*    <div className={cn(grid.pb_md, grid.w100)}>*/}
            {/*        <div className={cn(grid.pb_mini)}>{translate('securityInfoLifespan')}:</div>*/}
            {/*        <EditText*/}
            {/*            ref={(el) => (emailRefs.current['securityInfo'] = el)}*/}
            {/*            min={0}*/}
            {/*            tabindex={'0'}*/}
            {/*            className={cn(textStyles.center, styles.lifespanInput)}*/}
            {/*            value={state.lifespanD.securityInfo}*/}
            {/*            indentSize={'mini'}*/}
            {/*            type={'number'}*/}
            {/*            mod="withLightBorder"*/}
            {/*            onChange={(value: number) => {*/}
            {/*                onChangeLifespan(value, 'securityInfo')*/}
            {/*            }}*/}
            {/*            onFocus={() => {*/}
            {/*                handleFocus(-1, 'securityInfo')*/}
            {/*            }}*/}
            {/*            onBlur={() => {*/}
            {/*                handleFocus(-1, '')*/}
            {/*            }}*/}
            {/*            onEnterKeyDown={() => {*/}
            {/*                onConfirmEmailChange()*/}
            {/*            }}*/}
            {/*        />*/}
            {/*    </div>*/}
            {/*    {state.editingLifespanInput !== 'securityInfo' && (*/}
            {/*        <div className={styles.delete}>*/}
            {/*            <Icon type={'pencil'} onClick={() => focusByRefKey('securityInfo')} />*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*    {state.editingLifespanInput === 'securityInfo' && (*/}
            {/*        <div className={cn(styles.editButtons)}>*/}
            {/*            <div onMouseDown={() => onConfirmEmailChange()}>*/}
            {/*                <Icon type={'checked'} />*/}
            {/*            </div>*/}
            {/*            <div onMouseDown={() => onCancelEmailChange()}>*/}
            {/*                <Icon type={'close'} />*/}
            {/*            </div>*/}
            {/*        </div>*/}
            {/*    )}*/}
            {/*</div>*/}
            <div className={cn(styles.lifespanRow)}>
                <div className={cn(grid.pb_md, grid.w100)}>
                    <div className={cn(grid.pb_mini)}>{translate('emailHistoryLifespan')}:</div>
                    <EditText
                        ref={(el) => {
                            emailRefs.current.emailHistory = el
                        }}
                        min={0}
                        tabindex={'0'}
                        className={cn(textStyles.center, styles.lifespanInput)}
                        value={state.lifespanD.emailHistory}
                        indentSize={'mini'}
                        type={'number'}
                        mod="withLightBorder"
                        onChange={(value: number) => {
                            onChangeLifespan(value, 'emailHistory')
                        }}
                        onFocus={() => {
                            handleFocus(-1, 'emailHistory')
                        }}
                        onBlur={() => {
                            handleFocus(-1, '')
                        }}
                        onEnterKeyDown={() => {
                            onConfirmEmailChange()
                        }}
                    />
                </div>
                {state.editingLifespanInput !== 'emailHistory' && (
                    <div className={styles.delete}>
                        <Icon type={'pencil'} onClick={() => focusByRefKey('emailHistory')} />
                    </div>
                )}
                {state.editingLifespanInput === 'emailHistory' && (
                    <div className={cn(styles.editButtons)}>
                        <div onMouseDown={() => onConfirmEmailChange()}>
                            <Icon type={'checked'} />
                        </div>
                        <div onMouseDown={() => onCancelEmailChange()}>
                            <Icon type={'close'} />
                        </div>
                    </div>
                )}
            </div>
            {helpers.isAvailable({ key: 'settings', action: 'update' }) && (
                <div className={cn(grid.row, grid.center)}>
                    <Tippy
                        title={`${translate('maxNotificationsEmails')} ${maxNotificationsEmails}`}
                        open={!(state.notificationsEmails.length < maxNotificationsEmails) && state.isShowTooltip}
                    >
                        <Button
                            disabled={!(state.notificationsEmails.length < maxNotificationsEmails)}
                            onClick={addMail}
                            mod="withBorder"
                        >
                            {translate('addMail')}
                        </Button>
                    </Tippy>
                    <Button
                        className={cn(grid.ml_md)}
                        mod="fillAccent"
                        onClick={saveEmail}
                        disabled={!hasEmailChanges && !hasLifespanChanges && !state.needSave}
                    >
                        {translate('save')}
                    </Button>
                </div>
            )}
            <Modal open={state.confirmDeleteIndex !== null} showCloseIcon={false} mod={'clear'} transitionName={'fade'}>
                <div className={styles.modalWrapper}>
                    <div className={cn(styles.modalInner)}>
                        <div className={styles.closeIcon}>
                            <Icon
                                type={'close_tiny'}
                                onClick={() => {
                                    confirmDelete(null)
                                }}
                            />
                        </div>
                        <div className={styles.title}>{translate('deleteEmailTitle')}</div>
                        <div className={styles.description}>
                            <div>{translate('deleteEmailQuestion')}</div>
                            <div>{translate('deleteEmailTip')}</div>
                        </div>
                        <div className={styles.buttons}>
                            <Button
                                mod={'withBorder'}
                                className={cn(styles.leftButton)}
                                textSize={'mini'}
                                indentSize={'mini'}
                                rounded={'full_normal'}
                                onClick={() => confirmDelete(null)}
                            >
                                {translate('cancel')}
                            </Button>
                            <Button
                                mod={'fill'}
                                textSize={'mini'}
                                indentSize={'mini'}
                                rounded={'full_normal'}
                                onClick={deleteMail}
                            >
                                {translate('delete')}
                            </Button>
                        </div>
                    </div>
                </div>
            </Modal>
        </Collapse>
    )
}
